import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import PageHeader from "../../../components/layout/PageHeader";
import { useCompanies } from "../../../api/queries/organizationQueries";
import {
  useCutoffSettings,
  useRemoveCutoffRule,
  useSaveCutoffRule,
} from "../../../api/queries/cutoffSettingsQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatPlainDate, todayInManila } from "../../../utils/dateTime";
import {
  SOURCE_LABEL,
  WEEKDAYS,
  describeRule,
  formFrom,
  formProblems,
  toPayload,
} from "./cutoffSettingsFormat";
import "../admin.css";

const EMPTY = [];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};
const addDay = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};
const range = (p) =>
  `${formatPlainDate(p.start, "MMM D")} – ${formatPlainDate(p.end, "MMM D")}`;

const DayInput = ({ id, label, value, onChange, disabled }) => (
  <div>
    <label htmlFor={id} className="profile-label">
      {label}
    </label>
    <input
      id={id}
      type="number"
      min={1}
      max={28}
      className="form-control"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/**
 * Administration → DTR Cut-offs: the pay-period schedule per company (or the
 * global one). Changes start on a future date; past and locked DTRs keep theirs.
 */
const DtrCutoffSettingsPage = () => {
  const user = useCurrentUser();
  const { companyWide, superAdmin } = usePermissions();
  const canEdit = companyWide("company-setup", "modify");
  const { data: companies = EMPTY } = useCompanies();
  const [companyId, setCompanyId] = useState(
    user.companyId || (superAdmin ? "global" : ""),
  );
  const query = useCutoffSettings(companyId, { enabled: !!companyId });
  const save = useSaveCutoffRule();
  const remove = useRemoveCutoffRule();
  const busy = save.isPending || remove.isPending;
  const tomorrow = addDay(todayInManila());
  const d = query.data;

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  // New company: start the form from the schedule in force
  useEffect(() => {
    setForm(null);
    setErrors([]);
  }, [companyId]);
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const source = SOURCE_LABEL[d?.source];
  const target = companyId || "global";

  const submit = () => {
    const problems = formProblems(form, tomorrow);
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { companyId: target, rule: toPayload(form) },
      {
        onSuccess: () => {
          toast.success(
            `New schedule saved: it starts on ${formatPlainDate(form.effectiveFrom)}.`,
          );
          setForm(null);
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the schedule.")),
      },
    );
  };
  const removeRule = (rule) => {
    if (
      !window.confirm(
        `Cancel the change that starts on ${formatPlainDate(rule.effectiveFrom)}?`,
      )
    )
      return;
    remove.mutate(
      { companyId: target, effectiveFrom: rule.effectiveFrom },
      {
        onSuccess: () => toast.success("Scheduled change removed."),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not remove it.")),
      },
    );
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <PageHeader
          back={{ to: "/admin", label: "Administration" }}
          title="DTR Cut-offs"
          subtitle="When each pay period starts and which day weeks start on. Changes apply from a future date."
        />

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <label htmlFor="co-company" className="fs-080 text-slate-600">
            Company
          </label>
          <select
            id="co-company"
            className="status-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={!!form}
            title={form ? "Save or cancel the change first" : undefined}
          >
            {!companyId && <option value="">Choose a company…</option>}
            {superAdmin && (
              <option value="global">Global (every company)</option>
            )}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName || c.name}
              </option>
            ))}
          </select>
          {source && (
            <span className={`status-pill status-pill--${source.tone}`}>
              {d.source === "INHERITED" && d.inheritedFrom
                ? `Inherited from ${d.inheritedFrom.companyName}`
                : source.label}
            </span>
          )}
        </div>

        {!companyId ? (
          <div className="admin-empty">
            <i className="bi bi-buildings"></i>Choose a company first.
          </div>
        ) : query.isLoading ? (
          <div className="text-center py-5">
            <span
              className="spinner-border text-primary"
              role="status"
              aria-label="Loading"
            ></span>
          </div>
        ) : query.isError ? (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(query.error, "Could not load the schedule.")}
          </div>
        ) : (
          <div className="row g-3">
            <div className="col-xl-6 d-flex flex-column gap-3">
              <section className="admin-card">
                <h2 className="admin-card-title">
                  <i className="bi bi-calendar-check"></i>In force now
                </h2>
                <p className="fw-semibold mb-1">{describeRule(d.current)}</p>
                {d.current?.effectiveFrom && (
                  <p className="fs-075 text-slate-500 mb-0">
                    Since {formatPlainDate(d.current.effectiveFrom)}
                  </p>
                )}
                {d.rules && (
                  <p className="fs-075 text-slate-500 mt-2 mb-0">{d.rules}</p>
                )}
              </section>

              <section className="admin-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h2 className="admin-card-title m-0">
                    <i className="bi bi-calendar-plus"></i>Scheduled changes
                  </h2>
                  {canEdit && !form && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => setForm(formFrom(d.current, tomorrow))}
                    >
                      <i className="bi bi-plus-lg me-1"></i>Schedule a change
                    </button>
                  )}
                </div>
                {(d.upcoming || EMPTY).length === 0 && !form && (
                  <p className="fs-080 text-slate-500 mb-0">
                    No changes scheduled.
                  </p>
                )}
                <ul className="ns-list">
                  {(d.upcoming || EMPTY).map((r) => (
                    <li key={r.effectiveFrom} className="ns-row">
                      <div className="min-w-0">
                        <div className="fw-semibold fs-085">
                          From {formatPlainDate(r.effectiveFrom)}
                        </div>
                        <div className="fs-075 text-slate-500">
                          {describeRule(r)}
                        </div>
                      </div>
                      {canEdit && d.source === "OWN" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-light border text-danger"
                          onClick={() => removeRule(r)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {form && (
                  <div className="mt-3 pt-3 border-top">
                    {errors.length > 0 && (
                      <div className="alert alert-danger fs-085 py-2">
                        <ul className="mb-0 ps-3">
                          {errors.map((m) => (
                            <li key={m}>{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="profile-label">How often</div>
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      {[
                        ["SEMI_MONTHLY", "Twice a month"],
                        ["MONTHLY", "Once a month"],
                      ].map(([v, label]) => (
                        <div key={v} className="form-check">
                          <input
                            id={`co-freq-${v}`}
                            type="radio"
                            name="co-frequency"
                            className="form-check-input"
                            checked={form.frequency === v}
                            onChange={() => set({ frequency: v })}
                          />
                          <label
                            htmlFor={`co-freq-${v}`}
                            className="form-check-label fs-085"
                          >
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="row g-3">
                      {form.frequency === "MONTHLY" ? (
                        <div className="col-sm-6">
                          <DayInput
                            id="co-start"
                            label="Starts on day"
                            value={form.startDay}
                            onChange={(v) => set({ startDay: v })}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="col-sm-6">
                            <DayInput
                              id="co-first"
                              label="First period starts on day"
                              value={form.firstStartDay}
                              onChange={(v) => set({ firstStartDay: v })}
                            />
                          </div>
                          <div className="col-sm-6">
                            <DayInput
                              id="co-second"
                              label="Second period starts on day"
                              value={form.secondStartDay}
                              onChange={(v) => set({ secondStartDay: v })}
                            />
                          </div>
                        </>
                      )}
                      <div className="col-sm-6">
                        <label htmlFor="co-week" className="profile-label">
                          Weeks start on
                        </label>
                        <select
                          id="co-week"
                          className="form-select"
                          value={form.weekStartsOn}
                          onChange={(e) =>
                            set({ weekStartsOn: Number(e.target.value) })
                          }
                        >
                          {WEEKDAYS.map((w) => (
                            <option key={w.value} value={w.value}>
                              {w.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="co-from" className="profile-label">
                          Starts on (date)
                        </label>
                        <input
                          id="co-from"
                          type="date"
                          className="form-control"
                          min={tomorrow}
                          value={form.effectiveFrom}
                          onChange={(e) =>
                            set({ effectiveFrom: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <p className="fs-075 text-slate-500 mt-2">
                      Days 1–28 only. The cut-off running on the start date ends
                      the day before, so that month can have a short extra
                      cut-off. Submitted and locked DTRs keep their dates.
                    </p>
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary profile-btn"
                        onClick={() => {
                          setForm(null);
                          setErrors([]);
                        }}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary profile-btn"
                        onClick={submit}
                        disabled={busy}
                      >
                        {save.isPending && (
                          <span
                            className="spinner-border spinner-border-sm"
                            aria-hidden="true"
                          ></span>
                        )}
                        Save change
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="col-xl-6">
              <section className="admin-card h-100">
                <h2 className="admin-card-title">
                  <i className="bi bi-list-ol"></i>Next 6 cut-offs
                </h2>
                <p className="fs-075 text-slate-500">
                  With the saved schedule (scheduled changes included).
                </p>
                <ol className="co-preview">
                  {(d.preview || EMPTY).map((p) => (
                    <li key={p.key}>
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <strong className="fs-085">
                          {p.label || range(p)}
                        </strong>
                        {p.processOn && (
                          <span className="fs-075 text-slate-500">
                            Processed {formatPlainDate(p.processOn)}
                          </span>
                        )}
                      </div>
                      <div className="fs-075 text-slate-500">
                        {range(p)} · {(p.weeks || EMPTY).length} week
                        {(p.weeks || EMPTY).length === 1 ? "" : "s"}
                        {(p.weeks || EMPTY).some((w) => w.partial)
                          ? " (some split)"
                          : ""}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DtrCutoffSettingsPage;
